import { tw } from "@careinfo/tailwind";
import { useCallback, useContext, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { NOTIFICATION_OFFSET, NOTIFICATION_TYPE } from "./constants";
import { NotificationContext } from "./context";
import { Notification, NotificationSkeleton } from "./Notification";

export default function NotificationList() {
  const {
    filteredNotification,
    active,
    handleRead,
    isRefetching,
    handleNavigate,
    hasNext,
    searchTerm,
  } = useContext(NotificationContext);

  const [searchParams, setSearchParams] = useSearchParams();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const isPending = useRef(false);

  const handleOffsetChange = useCallback(() => {
    if (isRefetching || !hasNext || isPending.current) return;
    isPending.current = true;
    const clonedParams = new URLSearchParams(searchParams);
    const offset = Number(clonedParams.get(NOTIFICATION_OFFSET)) || 0;
    clonedParams.set(NOTIFICATION_OFFSET, String(offset + 10));
    setSearchParams(clonedParams);
  }, [
    isRefetching,
    hasNext,
    searchParams,
    setSearchParams,
    isPending,
    NOTIFICATION_OFFSET,
  ]);

  useEffect(() => {
    if (!loaderRef.current) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          handleOffsetChange();
        }
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0.1,
      }
    );

    observerRef.current.observe(loaderRef.current);
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleOffsetChange, filteredNotification.length]);

  useEffect(() => {
    const offset = Number(searchParams.get(NOTIFICATION_OFFSET)) || 0;
    if (offset === 0) {
      isPending.current = false;
    }
  }, [searchParams]);

  if (!isRefetching && isPending.current) {
    isPending.current = false;
  }

  return (
    <div className={tw`relative space-y-2 flex-1 overflow-y-scroll`}>
      {filteredNotification?.length > 0 ? (
        <>
          {filteredNotification.map((item) => (
            <Notification
              key={item.id}
              item={item}
              handleRead={handleRead}
              handleNavigate={handleNavigate}
              active={active}
            />
          ))}
          {isRefetching &&
            Array.from({ length: 5 }).map(() => (
              <NotificationSkeleton key={crypto.randomUUID()} />
            ))}

          {hasNext && !searchTerm && (
            <div
              key={searchParams.get(NOTIFICATION_TYPE)}
              ref={loaderRef}
              className="h-10 w-full"
            />
          )}
        </>
      ) : isRefetching ? (
        Array.from({ length: 10 }, () => (
          <NotificationSkeleton key={crypto.randomUUID()} />
        ))
      ) : (
        <p className={tw`text-lg text-gray-500 font-medium text-center py-4`}>
          No unread notifications
        </p>
      )}
    </div>
  );
}
